import { useFlags } from 'config/useFlagsContext'
import { addWeeks, endOfDay, isAfter, parseISO, startOfDay, subWeeks } from 'date-fns'
import { useLoadAntecedentesQuery } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import {
  LIMITE_INFERIOR_SEMANAS_GRAFICO_PRE_TERMO,
  LIMITE_SUPERIOR_SEMANAS_GRAFICO_PRE_TERMO,
} from 'view/atendimentos/detail/components/modals/medicoes/graficos/antropometria/util-grafico-pre-termo'

const NUM_SEMANAS_GRAFICO = LIMITE_SUPERIOR_SEMANAS_GRAFICO_PRE_TERMO - LIMITE_INFERIOR_SEMANAS_GRAFICO_PRE_TERMO

export interface UseIsCriancaPreTermoProps {
  prontuarioId: ID
  dataNascimento: LocalDate | undefined
  isSexoBinario: boolean
  idadeGestacional?: number
}

export interface IsCriancaPreTermoResult {
  isPreTermo: boolean
  isPreTermoNaFaixa64Semanas: boolean
  inicioFaixaPreTermo?: Date
  fimFaixaPreTermo?: Date
  idadeGestacionalSemanas?: number
}

const resolveIdadeGestacionalSemanas = (
  idadeGestacionalAtendimentoAtual: number | undefined,
  idadeGestacionalSemanasAntecedente: string | undefined
) => {
  if (idadeGestacionalAtendimentoAtual != null && idadeGestacionalAtendimentoAtual > 0) {
    return idadeGestacionalAtendimentoAtual
  }

  const idadeGestacionalAntecedente = idadeGestacionalSemanasAntecedente
    ? parseInt(idadeGestacionalSemanasAntecedente)
    : undefined

  return idadeGestacionalAntecedente != null && idadeGestacionalAntecedente > 0
    ? idadeGestacionalAntecedente
    : undefined
}

export const useIsCriancaPreTermo = (props: UseIsCriancaPreTermoProps): IsCriancaPreTermoResult => {
  const { prontuarioId, dataNascimento, isSexoBinario, idadeGestacional } = props
  const { PILOTO_GRAFICOS_PRE_TERMO_ENABLED } = useFlags()

  const { data } = useLoadAntecedentesQuery({
    variables: { id: prontuarioId },
    skip:
      !PILOTO_GRAFICOS_PRE_TERMO_ENABLED || !prontuarioId || !dataNascimento || !isSexoBinario || !!idadeGestacional,
  })
  const idadeGestacionalSemanasAntecedente = data?.antecedente?.idadeGestacionalSemanas

  return useMemo(() => {
    const weeks = resolveIdadeGestacionalSemanas(idadeGestacional, idadeGestacionalSemanasAntecedente)

    if (!PILOTO_GRAFICOS_PRE_TERMO_ENABLED || !isSexoBinario || !dataNascimento || weeks == null) {
      return { isPreTermo: false, isPreTermoNaFaixa64Semanas: false }
    }

    const isPreTermo = weeks > 0 && weeks < 37

    if (!isPreTermo) {
      return { isPreTermo: false, isPreTermoNaFaixa64Semanas: false }
    }

    const dataNascimentoParsed = parseISO(dataNascimento)
    const diferencaSemanas = weeks - LIMITE_INFERIOR_SEMANAS_GRAFICO_PRE_TERMO
    const inicioFaixaPreTermo = startOfDay(
      diferencaSemanas >= 0
        ? subWeeks(dataNascimentoParsed, diferencaSemanas)
        : addWeeks(dataNascimentoParsed, Math.abs(diferencaSemanas))
    )
    const fimFaixaPreTermo = endOfDay(addWeeks(inicioFaixaPreTermo, NUM_SEMANAS_GRAFICO))
    const isPreTermoNaFaixa64Semanas = !isAfter(new Date(), fimFaixaPreTermo)

    return {
      isPreTermo,
      isPreTermoNaFaixa64Semanas,
      inicioFaixaPreTermo,
      fimFaixaPreTermo,
      idadeGestacionalSemanas: weeks,
    }
  }, [
    idadeGestacional,
    idadeGestacionalSemanasAntecedente,
    dataNascimento,
    PILOTO_GRAFICOS_PRE_TERMO_ENABLED,
    isSexoBinario,
  ])
}
